import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte, BokkzAlgDataService, Gift,
    GiftPeriodiek, IncomeTaxEntity,
    PGAAftrekbareGiften
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { filter, Subscription } from 'rxjs';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-periodieke-giften',
    templateUrl: './periodieke-giften.component.html',
    styleUrls: ['./periodieke-giften.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        CommonModule
    ]
})
export class PeriodiekeGiftenComponent implements OnInit, OnDestroy {
    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isDefAangifteSubscription: Subscription | undefined;
    private isSaving = false;
    public clientId!: string;

    @Input() public id!: string;
    public aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    private focusedElementId: string | null = null;

    public periodiekeGiftenFg = {} as FormGroup;

    public isDefAangifte = {} as boolean;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService, private router: Router, private fb: FormBuilder) {
        this.periodiekeGiftenFg = this.fb.group({
            periodiekeGiften:
                this.fb.array<PGAAftrekbareGiften>([])
        });
    }

    get periodiekeGiften(): FormArray {
        return this.periodiekeGiftenFg.get('periodiekeGiften') as FormArray;
    }

    addRijPeriodiekGiften(): void {
        this.periodiekeGiften.push(this.createPeriodiekeGiftenFormGroup());
    }

    verwijderRijPeriodiekGiften(index: number): void {
        this.periodiekeGiften.removeAt(index);
        this.periodiekeGiftenFg.updateValueAndValidity();
        this.periodiekeGiftenFg.markAsDirty();
        this.saveForm();
    }

    createPeriodiekeGiftenFormGroup(giftP?: GiftPeriodiek): FormGroup {
        return this.fb.group({
            omschrijving: giftP?.omschrijving ?? undefined,
            bedrag: giftP?.bedrag ?? undefined,
            cultureleAnbi: giftP?.cultureleAnbi ?? false,
            transactienummer: giftP?.transactienummer ?? undefined,
            rsin: giftP?.rsin ?? undefined
        } as GiftPeriodiek);
    }

    public ngOnInit(): void {
        this.isDefAangifteSubscription = this.dataService.isDefAangifte.subscribe((isDefAangifte) => {
            this.isDefAangifte = isDefAangifte;
        });
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte;
                this.aangifte = aangifte;
                this.updateForm();
            }
        });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.routerSubscription = this.router.events
            .pipe(
                filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
        if (this.isDefAangifteSubscription) {
            this.isDefAangifteSubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.periodiekeGiftenFg.dirty;
    }

    private updateForm(): void {
        const perodiekeGiftenLijst = this.aangifte?.persoonsgebondenAftrek?.pgaAftrekbareGiften?.periodiekeGiften || [];
        const formArray = this.periodiekeGiftenFg.get('periodiekeGiften') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > perodiekeGiftenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Add or update controls
        for (let i = 0; i < perodiekeGiftenLijst.length; i++) {
            if (i < formArray.length) {
                // Update existing control
                const control = formArray.at(i) as FormGroup;
                control.patchValue(perodiekeGiftenLijst[i]);
            } else {
                // Add new control
                const formGroup = this.createPeriodiekeGiftenFormGroup(perodiekeGiftenLijst[i]);
                formArray.push(formGroup);
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {

        const periodiekeGiften = (this.periodiekeGiftenFg.value.periodiekeGiften as Gift[])
            .map(gift => ({
                ...gift,
                bedrag: gift.omschrijving?.trim() && !gift.bedrag ? 0 : gift.bedrag,
                transactienummer: gift['transactienummer'] ? Number(gift['transactienummer']) : undefined
            }));

        this.incomeTaxEntity.aangifte = {
            ...this.aangifte,
            persoonsgebondenAftrek: {
                ...this.aangifte.persoonsgebondenAftrek,
                pgaAftrekbareGiften: {
                    ...this.aangifte.persoonsgebondenAftrek?.pgaAftrekbareGiften,
                    periodiekeGiften: periodiekeGiften
                }
            }
        } as Aangifte;
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    }
}
